/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Buildings, ChartLineUp, Leaf, FileText, CurrencyCircleDollar,
  Gauge, Globe, ArrowRight, ShieldCheck, Factory, Storefront, Student,
  PresentationChart, Recycle,
} from "@phosphor-icons/react";
import {
  MarketingNav, MarketingFooter, Eyebrow, FeatureCard, BulletList,
  CtaBanner, BTN_GREEN,
} from "./_shared";

export default function ForCompanies() {
  useEffect(() => {
    document.title = "Rescigo · Enterprise Waste Intelligence & ESG Reporting";
    window.scrollTo(0, 0);
  }, []);
  return (
    <div className="min-h-screen bg-white" data-testid="page-for-companies">
      <MarketingNav/>

      {/* HERO */}
      <section className="relative pt-36 pb-24 overflow-hidden bg-gradient-to-b from-slate-50 to-white">
        <div className="absolute inset-0" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, rgba(10,37,64,0.06) 0%, transparent 45%)" }}/>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 grid md:grid-cols-2 gap-14 items-center">
          <div>
            <Eyebrow color="#0A2540">Enterprise · ESG · Compliance</Eyebrow>
            <h1 className="font-heading text-5xl md:text-6xl mt-4 leading-[1.05] text-slate-900">
              Waste intelligence, <span className="text-[#0A2540] italic">ESG reporting</span> & compliance — simplified.
            </h1>
            <p className="text-lg text-slate-600 mt-6 leading-relaxed max-w-xl">
              Every gram of waste tracked, every kilogram diverted accounted for.
              Hand-off ready CSR/BRSR/GRI disclosures, benchmark reports, and
              board-grade compliance dashboards.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/#contact" data-testid="co-cta-demo" className={BTN_GREEN}>
                Book enterprise demo <ArrowRight size={14} weight="bold"/>
              </Link>
              <Link to="/white-papers" data-testid="co-cta-wp"
                    className="inline-flex items-center gap-2 h-12 px-5 rounded-full border border-slate-300 text-slate-800 hover:border-slate-400 text-sm font-medium">
                ESG white papers
              </Link>
            </div>
            <div className="mt-10 flex items-center gap-5 text-[11px] uppercase tracking-[0.2em] text-slate-500">
              <span className="flex items-center gap-2"><ShieldCheck size={13} weight="fill" className="text-[#0A2540]"/>BRSR ready</span>
              <span>·</span><span>GRI 306</span><span>·</span><span>SASB aligned</span>
            </div>
          </div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
                       className="relative grid grid-cols-2 gap-3">
            <TileCard Icon={Leaf} title="Waste diverted" v="86.4 t" sub="+12% MoM" color="#2D5A27"/>
            <TileCard Icon={Recycle} title="Recycled" v="72%" sub="target 70%" color="#0A2540"/>
            <TileCard Icon={Globe} title="CO₂ saved" v="214 t" sub="YTD" color="#2D5A27"/>
            <TileCard Icon={CurrencyCircleDollar} title="Cost saved" v="₹38L" sub="vs baseline" color="#0A2540"/>
            <div className="col-span-2 bg-white rounded-2xl ring-1 ring-slate-200 p-5">
              <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2">BRSR Dashboard · Q3 2026</div>
              <LineChartSVG/>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-2xl">
            <Eyebrow color="#0A2540">Capabilities</Eyebrow>
            <h2 className="font-heading text-4xl md:text-5xl mt-4 text-slate-900">
              Built for CFOs, Sustainability heads & COOs.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5 mt-12">
            <FeatureCard accent="#0A2540" Icon={ChartLineUp} title="Waste Accounting Automation" desc="Unit-level capture across every facility. Auto-rolled into monthly ledgers and annual disclosures."/>
            <FeatureCard accent="#0A2540" Icon={PresentationChart} title="ESG Dashboards" desc="Board-grade dashboards aligned to BRSR, GRI 306, SASB. Export to PDF, CSV, XBRL."/>
            <FeatureCard accent="#0A2540" Icon={FileText} title="Compliance Reporting" desc="One-click regulatory reports — CPCB, SPCB, local pollution board, insurance audits."/>
            <FeatureCard accent="#2D5A27" Icon={CurrencyCircleDollar} title="Cost Reduction" desc="Identify leakage, vendor rate anomalies, and over-allocation. Avg 11–18% cost saving in year one."/>
            <FeatureCard accent="#2D5A27" Icon={Leaf} title="Sustainability Tracking" desc="SDG-mapped KPIs, carbon equivalents, water & energy co-benefits — all in one dashboard."/>
            <FeatureCard accent="#2D5A27" Icon={Globe} title="Circular Economy Insights" desc="Recycled-ratio benchmarking vs industry peers. Identify circular revenue opportunities."/>
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="py-24 bg-[#F7F6F2]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
            <div>
              <Eyebrow color="#0A2540">Industries we serve</Eyebrow>
              <h2 className="font-heading text-4xl md:text-5xl mt-4 text-slate-900">Deployed across verticals.</h2>
            </div>
          </div>
          <div className="grid md:grid-cols-4 gap-5">
            <IndustryCard Icon={Buildings} title="Apartments & Housing Societies" desc="Segregation-at-source, QR bag programs, resident engagement, RWA dashboards."/>
            <IndustryCard Icon={Storefront} title="Commercial Complexes" desc="Per-tenant accounting, tenant billing, shared-infrastructure compliance."/>
            <IndustryCard Icon={Factory} title="Manufacturing Units" desc="Hazardous waste manifest, EPR compliance, by-product tracking."/>
            <IndustryCard Icon={Student} title="Institutions & Campuses" desc="Zero-waste targets, staff training, campus-wide analytics."/>
          </div>
        </div>
      </section>

      {/* ROI / VALUE */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid md:grid-cols-2 gap-14 items-center">
          <div>
            <Eyebrow color="#0A2540">Measured ROI</Eyebrow>
            <h2 className="font-heading text-4xl md:text-5xl mt-4 text-slate-900">
              Every rupee spent on waste intelligence returns <span className="text-[#2D5A27] italic">3–5x</span>.
            </h2>
            <p className="text-slate-600 mt-4 leading-relaxed">
              Our enterprise customers see measurable reductions within 90 days —
              in waste volume, vendor costs, and non-compliance exposure. We show
              every data point.
            </p>
            <div className="mt-6"><BulletList color="#0A2540" items={[
              "11–18% reduction in waste handling cost (avg Year 1)",
              "2.4x improvement in segregation compliance",
              "Zero missed regulatory filings (automated deadlines)",
              "34% lift in employee participation via gamified programs",
              "Insurance premium discount via verified audit trail",
            ]}/></div>
          </div>
          <div className="bg-gradient-to-br from-[#0A2540] to-[#0E3358] text-white rounded-2xl p-10 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#2D5A27]/20 rounded-full blur-3xl"/>
            <div className="relative">
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/60 mb-2">Year-1 results</div>
              <div className="space-y-5">
                <ResultRow k="Waste volume" baseline="142 t" now="91 t" delta="-36%"/>
                <ResultRow k="Handling cost" baseline="₹1.14 Cr" now="₹94 L" delta="-17.5%"/>
                <ResultRow k="Recycled ratio" baseline="42%" now="71%" delta="+29 pts" up/>
                <ResultRow k="Audit findings" baseline="14" now="2" delta="-86%"/>
              </div>
              <div className="mt-8 pt-6 border-t border-white/10 text-xs text-white/60">
                Case study · Manufacturing conglomerate · 14 facilities across 4 states · Jan–Dec 2025
              </div>
            </div>
          </div>
        </div>
      </section>

      <CtaBanner
        eyebrow="Enterprise Deployment"
        title="Turn your waste data into a board-level asset."
        body="We handle onboarding, integration, and your first ESG disclosure cycle — end-to-end."
        primary={{ label: "Book enterprise demo", to: "/#contact", testid: "co-cta-bottom" }}
        secondary={{ label: "Download pricing", to: "/white-papers" }}
      />

      <MarketingFooter/>
    </div>
  );
}

const TileCard = ({ Icon, title, v, sub, color }) => (
  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl ring-1 ring-slate-200 p-5">
    <Icon size={16} weight="duotone" style={{ color }}/>
    <div className="font-heading text-3xl mt-3" style={{ color }}>{v}</div>
    <div className="text-xs text-slate-700 mt-1 font-medium">{title}</div>
    <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>
  </motion.div>
);

const IndustryCard = ({ Icon, title, desc }) => (
  <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl ring-1 ring-slate-200 p-7 hover:shadow-lg transition">
    <Icon size={28} weight="duotone" className="text-[#0A2540]"/>
    <h3 className="font-heading text-lg text-slate-900 mt-4">{title}</h3>
    <p className="text-sm text-slate-600 mt-2 leading-relaxed">{desc}</p>
  </motion.div>
);

const ResultRow = ({ k, baseline, now, delta, up }) => (
  <div className="grid grid-cols-4 items-center gap-3 text-sm">
    <div className="text-white/70">{k}</div>
    <div className="line-through text-white/40 font-mono text-xs">{baseline}</div>
    <div className="text-white font-heading text-lg">{now}</div>
    <div className={`text-xs font-semibold font-mono ${up ? "text-emerald-300" : "text-amber-300"}`}>{delta}</div>
  </div>
);

const LineChartSVG = () => (
  <svg viewBox="0 0 300 80" className="w-full h-20">
    <defs>
      <linearGradient id="area" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0%" stopColor="#2D5A27" stopOpacity="0.3"/>
        <stop offset="100%" stopColor="#2D5A27" stopOpacity="0"/>
      </linearGradient>
    </defs>
    <path d="M0,60 C30,55 60,48 100,40 C140,32 180,36 220,22 C260,12 290,8 300,5 L300,80 L0,80 Z" fill="url(#area)"/>
    <path d="M0,60 C30,55 60,48 100,40 C140,32 180,36 220,22 C260,12 290,8 300,5" fill="none" stroke="#2D5A27" strokeWidth="2"/>
    <circle cx="300" cy="5" r="3" fill="#2D5A27"/>
  </svg>
);
